import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import apiClient from '@/services/api';
import { useToast } from '@/components/common/Toast';

/**
 * The road networks catchments are searched on and the countries they serve (/api/v2/admin/routing). A network's
 * minutes come from its traffic rules (rules.gx.traffic.*, edited on the Taxonomy screen); "Price again" writes
 * them anew. A country says which network it uses, whether its catchments may cross its border by default, and
 * the layers that find a place's country (when the borders are not loaded), give its demographics and its places.
 */
interface NetworkRow {
  key: string;
  name: string;
  table: string;
  orientation: string;
  trafficRules: string;
  tileDegrees: number;
  memoryMb: number;
  direction: string;
  roads: number | null;
  tilesHeld: number;
  mbHeld: number;
  pricing?: string | null;
  pricedWith?: string;
  pricedRoads?: number;
  pricedAt?: string;
}

interface CountryRow {
  code: string;
  name: string;
  network: string;
  crossBorder: boolean;
  detectLayer: string | null;
  demographicsLayer: string | null;
  placeLayer: string | null;
  position: number;
}

interface Overview {
  networks: NetworkRow[];
  countries: CountryRow[];
  rules: string[];
  borders: number | null;
}

const BASE = '/v2/admin/routing';
const fmt = (n?: number | null) => (n === null || n === undefined ? '–' : n.toLocaleString());

const RoutingManagement: React.FC = () => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<CountryRow | null>(null);
  const [filter, setFilter] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['adminRouting'],
    queryFn: async () => (await apiClient.get<Overview>(BASE)).data,
    refetchInterval: (q) => (q.state.data?.networks.some((n) => n.pricing) ? 5000 : false),
  });
  const refresh = (o?: Overview) => {
    if (o) queryClient.setQueryData(['adminRouting'], o);
    else queryClient.invalidateQueries({ queryKey: ['adminRouting'] });
  };
  const failed = () => addToast(t('routing.failed'), 'error');

  const price = useMutation({
    mutationFn: ({ key, onlyMissing }: { key: string; onlyMissing: boolean }) =>
      apiClient.post(`${BASE}/networks/${key}/price`, null, { params: { onlyMissing } }),
    onSuccess: () => { addToast(t('routing.pricing_started'), 'success'); setTimeout(() => refresh(), 1500); },
    onError: failed,
  });
  const forget = useMutation({
    mutationFn: async () => (await apiClient.post<Overview>(`${BASE}/forget`)).data,
    onSuccess: (o) => { refresh(o); addToast(t('routing.forgotten'), 'success'); },
    onError: failed,
  });
  const save = useMutation({
    mutationFn: async (c: CountryRow) => (await apiClient.put<Overview>(`${BASE}/countries/${c.code}`, {
      name: c.name,
      network: c.network,
      crossBorder: c.crossBorder,
      detectLayer: c.detectLayer || null,
      demographicsLayer: c.demographicsLayer || null,
      placeLayer: c.placeLayer || null,
      position: Number(c.position) || 0,
    })).data,
    onSuccess: (o) => { refresh(o); setEditing(null); addToast(t('routing.saved'), 'success'); },
    onError: failed,
  });

  const countries = (data?.countries ?? []).filter((c) => !filter
    || `${c.code} ${c.name}`.toLowerCase().includes(filter.toLowerCase()));

  return (
    <div className="max-w-6xl space-y-8">
      <div>
        <h2 className="text-xl font-bold mb-1">{t('routing.title')}</h2>
        <p className="text-sm text-gn-muted">{t('routing.hint')}</p>
      </div>
      {isLoading && <p>…</p>}
      {error && <p className="text-red-600 text-sm">{t('routing.failed')}</p>}

      {data && (
        <section>
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold">{t('routing.networks')}</h3>
            <button type="button" className="px-3 py-1 border rounded text-sm" onClick={() => forget.mutate()}
              title={t('routing.forget_hint')}>{t('routing.forget')}</button>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gn-muted">
                <th>{t('routing.network')}</th><th>{t('routing.table')}</th><th>{t('routing.roads')}</th>
                <th>{t('routing.rules')}</th><th>{t('routing.priced')}</th><th>{t('routing.held')}</th><th />
              </tr>
            </thead>
            <tbody>
              {data.networks.map((n) => (
                <tr key={n.key} className="border-t border-gn-light align-top">
                  <td className="py-2"><div className="font-medium">{n.name}</div><div className="text-xs text-gn-muted">{n.key}</div></td>
                  <td className="py-2 font-mono text-xs">{n.table}<div className="text-gn-muted">
                    {n.tileDegrees > 0 ? t('routing.tiled', { deg: n.tileDegrees, mb: n.memoryMb }) : t('routing.whole')}</div></td>
                  <td className="py-2">{fmt(n.roads)}</td>
                  <td className="py-2">
                    <Link className="text-gn-blue underline" to="/admin/taxonomy">{n.trafficRules}</Link>
                  </td>
                  <td className="py-2 text-xs">
                    {n.pricing ? <span className="text-gn-blue">{t('routing.pricing_now', { what: n.pricing })}</span>
                      : n.pricedAt ? <>{n.pricedAt}<div className="text-gn-muted">{fmt(n.pricedRoads)} · {n.pricedWith}</div></>
                        : <span className="text-gn-muted">{t('routing.not_priced')}</span>}
                  </td>
                  <td className="py-2 text-xs">{n.tilesHeld} · {n.mbHeld} MB</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button type="button" className="px-3 py-1 border rounded mr-1" disabled={!!n.pricing}
                      onClick={() => price.mutate({ key: n.key, onlyMissing: false })}>{t('routing.price')}</button>
                    <button type="button" className="px-3 py-1 border rounded" disabled={!!n.pricing}
                      onClick={() => price.mutate({ key: n.key, onlyMissing: true })}>{t('routing.price_missing')}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-gn-muted mt-2">{t('routing.borders', { n: fmt(data.borders) })}</p>
        </section>
      )}

      {data && (
        <section>
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold">{t('routing.countries')}</h3>
            <input className="border rounded px-2 py-1 text-sm" placeholder={t('routing.search')} value={filter}
              onChange={(e) => setFilter(e.target.value)} />
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gn-muted">
                <th>{t('routing.code')}</th><th>{t('routing.name')}</th><th>{t('routing.network')}</th>
                <th>{t('routing.cross_border')}</th><th>{t('routing.layers')}</th><th />
              </tr>
            </thead>
            <tbody>
              {countries.map((c) => (
                <tr key={c.code} className="border-t border-gn-light">
                  <td className="py-2 font-mono">{c.code}</td>
                  <td className="py-2">{c.name}</td>
                  <td className="py-2">{c.network}</td>
                  <td className="py-2">{c.crossBorder ? t('routing.allow') : t('routing.stay')}</td>
                  <td className="py-2 text-xs text-gn-muted">
                    {[c.detectLayer, c.demographicsLayer, c.placeLayer].filter(Boolean).join(' · ') || '–'}
                  </td>
                  <td className="py-2 text-right">
                    <button type="button" className="px-3 py-1 border rounded" onClick={() => setEditing({ ...c })}>
                      {t('routing.edit')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {editing && data && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={() => setEditing(null)}>
          <form className="bg-gn-primary rounded shadow-lg p-6 w-[28rem] space-y-3 text-sm" onClick={(e) => e.stopPropagation()}
            onSubmit={(e) => { e.preventDefault(); save.mutate(editing); }}>
            <h3 className="font-semibold">{editing.code}</h3>
            <label className="block">{t('routing.name')}
              <input className="w-full border rounded px-2 py-1" value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></label>
            <label className="block">{t('routing.network')}
              <select className="w-full border rounded px-2 py-1" value={editing.network}
                onChange={(e) => setEditing({ ...editing, network: e.target.value })}>
                {data.networks.map((n) => <option key={n.key} value={n.key}>{n.name}</option>)}
              </select></label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={editing.crossBorder}
                onChange={(e) => setEditing({ ...editing, crossBorder: e.target.checked })} />
              {t('routing.cross_border_hint')}</label>
            <label className="block">{t('routing.detect_layer')}
              <input className="w-full border rounded px-2 py-1" value={editing.detectLayer ?? ''}
                onChange={(e) => setEditing({ ...editing, detectLayer: e.target.value })} /></label>
            <label className="block">{t('routing.demographics_layer')}
              <input className="w-full border rounded px-2 py-1" value={editing.demographicsLayer ?? ''}
                onChange={(e) => setEditing({ ...editing, demographicsLayer: e.target.value })} /></label>
            <label className="block">{t('routing.place_layer')}
              <input className="w-full border rounded px-2 py-1" value={editing.placeLayer ?? ''}
                onChange={(e) => setEditing({ ...editing, placeLayer: e.target.value })} /></label>
            <label className="block">{t('routing.position')}
              <input type="number" className="w-full border rounded px-2 py-1" value={editing.position}
                onChange={(e) => setEditing({ ...editing, position: Number(e.target.value) })} /></label>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="px-3 py-1 border rounded" onClick={() => setEditing(null)}>{t('routing.cancel')}</button>
              <button type="submit" className="px-3 py-1 border rounded bg-gn-blue-light border-gn-blue text-gn-blue"
                disabled={save.isPending}>{t('routing.save')}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default RoutingManagement;
